<div class="notification-inbox">
  <button
    type="button"
    class="notification-inbox-trigger"
    pStyleClass="@next"
    enterFromClass="hidden"
    enterActiveClass="animate-scalein"
    leaveActiveClass="animate-fadeout"
    leaveToClass="hidden"
    [hideOnOutsideClick]="true"
    aria-haspopup="true"
    [attr.aria-label]="unreadCount() > 0 ? 'Notificaciones, ' + unreadCount() + ' sin leer' : 'Notificaciones'"
    (click)="onOpen()">
    @if (unreadCount() > 0) {
      <p-overlay-badge [value]="unreadCount()" severity="danger" [pt]="unreadBadgePt">
        <i class="pi pi-bell"></i>
      </p-overlay-badge>
    } @else {
      <i class="pi pi-bell"></i>
    }
  </button>

  <div class="notification-inbox-panel hidden" role="menu" aria-label="Notificaciones">
    <div class="notification-inbox-header">
      <span>Notificaciones</span>
      @if (unreadCount() > 0) {
        <p-button
          label="Marcar todas como leídas"
          [text]="true"
          [link]="true"
          size="small"
          (onClick)="onMarkAllAsRead()" />
      }
    </div>

    <div
      class="notification-inbox-tabs flex items-center gap-2 rounded-full p-1 border border-surface-200 dark:border-surface-800 bg-white/55 dark:bg-white/8 shadow-custom-shadow"
      role="tablist"
      aria-label="Filtro de notificaciones">
      @for (tab of filters; track tab.value) {
        <button
          type="button"
          role="tab"
          [attr.aria-selected]="filter() === tab.value"
          [class]="tabButtonClass(tab.value)"
          (click)="onFilterChange(tab.value)">
          <span class="capitalize font-medium text-surface-950 dark:text-surface-0">{{ tab.label }}</span>
        </button>
      }
    </div>

    <p-scroll-panel [style]="{ width: '100%', height: '22rem' }" styleClass="notification-inbox-body">
      @if (loading()) {
        <div class="notification-inbox-state" data-testid="notification-inbox-loading">
          <p-progress-spinner styleClass="notification-inbox-spinner" strokeWidth="4" ariaLabel="Cargando" />
          <span>Cargando notificaciones…</span>
        </div>
      } @else if (error()) {
        <p-message severity="error" styleClass="w-full" data-testid="notification-inbox-error">
          <div class="notification-inbox-message">
            <span>No se pudieron cargar las notificaciones.</span>
            <p-button label="Reintentar" [text]="true" [link]="true" size="small" (onClick)="onOpen()" />
          </div>
        </p-message>
      } @else if (isEmpty()) {
        <div class="notification-inbox-empty" data-testid="notification-inbox-empty">
          <i class="pi pi-inbox" aria-hidden="true"></i>
          <span>No tienes notificaciones.</span>
        </div>
      } @else {
        <ul class="notification-inbox-list" data-testid="notification-inbox-list">
          @for (notification of notifications(); track notification.getId()) {
            <li class="notification-inbox-item" [class.unread]="!notification.getIsRead()">
              <div
                class="notification-inbox-item-trigger"
                role="menuitem"
                tabindex="0"
                (click)="onNotificationClick(notification)"
                (keyup.enter)="onNotificationClick(notification)">
                <p-avatar
                  [icon]="notificationIcon(notification)"
                  shape="circle"
                  [styleClass]="notificationTypeClass(notification)" />
                <div class="notification-inbox-item-content">
                  <div class="notification-inbox-item-top">
                    @if (notification.getSubject()) {
                      <span class="notification-inbox-item-subject">{{ notification.getSubject() }}</span>
                    }
                    @if (!notification.getIsRead()) {
                      <span class="notification-inbox-item-dot" aria-hidden="true"></span>
                    }
                  </div>
                  @if (notification.getTags().length > 0) {
                    <div class="notification-inbox-item-tags">
                      @for (tag of notification.getTags(); track tag) {
                        <app-tag [severity]="tagSeverity" type="outline" size="small">{{ tag }}</app-tag>
                      }
                    </div>
                  }
                  <div class="notification-inbox-item-card" [innerHTML]="renderBody(notification.getBody())"></div>
                  @if (notification.getPrimaryAction() || notification.getSecondaryAction()) {
                    <div class="notification-inbox-item-actions">
                      @if (notification.getSecondaryAction(); as secondaryAction) {
                        <p-button
                          [label]="secondaryAction.label"
                          [text]="true"
                          [disabled]="secondaryAction.isCompleted"
                          size="small"
                          severity="secondary"
                          (onClick)="onCompleteAction($event, notification, actionType.SECONDARY)" />
                      }
                      @if (notification.getPrimaryAction(); as primaryAction) {
                        <p-button
                          [label]="primaryAction.label"
                          [disabled]="primaryAction.isCompleted"
                          size="small"
                          (onClick)="onCompleteAction($event, notification, actionType.PRIMARY)" />
                      }
                    </div>
                  }
                  <div class="notification-inbox-item-footer">
                    <span class="notification-inbox-item-date">{{
                      notification.getCreatedAt() | moment: 'fromNow'
                    }}</span>
                    <div class="notification-inbox-item-footer-actions">
                      @if (!notification.getIsRead()) {
                        <p-button
                          [text]="true"
                          [rounded]="true"
                          size="small"
                          severity="secondary"
                          icon="pi pi-check"
                          ariaLabel="Marcar como leída"
                          (onClick)="onMarkAsRead($event, notification)" />
                      }
                      <p-button
                        [text]="true"
                        [rounded]="true"
                        size="small"
                        severity="secondary"
                        [icon]="filter() === 'archived' ? 'pi pi-refresh' : 'pi pi-box'"
                        [ariaLabel]="filter() === 'archived' ? 'Restaurar notificación' : 'Archivar notificación'"
                        (onClick)="onArchiveToggle($event, notification)" />
                      <p-button
                        [text]="true"
                        [rounded]="true"
                        size="small"
                        severity="danger"
                        icon="pi pi-trash"
                        ariaLabel="Eliminar notificación"
                        (onClick)="onDelete($event, notification)" />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          }
        </ul>
        @if (hasMore()) {
          <div class="notification-inbox-load-more">
            <p-button
              label="Cargar más"
              [text]="true"
              [link]="true"
              size="small"
              [loading]="loadingMore()"
              (onClick)="onLoadMore()" />
          </div>
        }
      }
    </p-scroll-panel>
  </div>
</div>
